// src/lib/schema/base/product.js

import { shopInfoContainer } from '@/containers/shopInfo.container';
import { allowedCountries } from '@/containers/shopInfo.container';
import { validateRequiredFields, isValidPrice, isValidImage, getValidUntilDate } from '@/lib/schema';


/**
 * Get the category string for the product schema based on product type
 * @param {object} product - Product data
 * @returns {string} - Category string for schema
 */
function getCategoryString(product) {
  const category = product.category || 'carpets';

  switch (category.toLowerCase()) {
    case 'carpets':
    case 'rugs':
      return `Carpet > ${product.style || 'Decorative'} > ${product.origins || 'Imported'}`;
    // Add more categories as needed
    default:
      return `${category.charAt(0).toUpperCase() + category.slice(1)} > ${product.subCategory || 'General'}`;
  }
}


/**
 * Creates structured data for a product page
 * @param {object} product - Product object with details
 * @param {string} lang - Language code
 * @param {string} url - Full URL of the product page (optional)
 * @returns {object|null} - JSON-LD schema for the product or null if invalid
 */
export function generateProductSchema(product, lang, url = null) {
  // Validate required fields
  if (!validateRequiredFields(product, ['titles', 'productID'])) {
    console.warn('Missing required product fields for schema generation');
    return null;
  }

  // Get shop info for organization data
  const { baseURL, name: shopName } = shopInfoContainer;

  // Determine the unit system based on language
  const unitSystem = lang === 'en' ? 'USC' : 'SI';

  // Get correct product title based on unit system
  const titles = product.titles[unitSystem] || {};
  const productTitle = product?.titleAtCheckout || titles?.standard || product?.title || '';

  // Determine product category (default to 'carpets' for backward compatibility)
  const productCategory = product.category || 'carpets';

  // Create product URL if not provided
  const productURL = url || `${baseURL}/${lang}/shop/${productCategory}/${product.titles.url || product.titleAtURL || product.productID}`;

  // Generate price valid until date (1 year from now)
  const priceValidUntil = getValidUntilDate(1, 'year');

  // Base schema structure
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    'name': productTitle,
    'description': titles?.altTag?.detailed || titles?.altTag?.basic || product.description || '',
    'image': getProductImages(product),
    'sku': product?.sku || product?.productID,
    'mpn': product?.mpn || product?.productID,
    'productID': product.productID,
    'url': productURL,
    'brand': {
      '@type': 'Brand',
      'name': product.brand || 'YourCarpet.com'
    },
    'category': getCategoryString(product),
  };

  // Validate images
  if (!isValidImage(schema.image)) {
    console.warn('Invalid product image(s) in schema');
  }

  // Add material if available
  if (product.materials && product.materials.length) {
    schema.material = Array.isArray(product.materials)
      ? product.materials.join(', ')
      : product.titleMaterials || product.materials;
  }

  // Add color if available
  if (product.mainColors || product.titleMainColors) {
    schema.color = product.titleMainColors ||
      (Array.isArray(product.mainColors) ? product.mainColors.join(', ') : product.mainColors);
  }

  // Add dimensions based on product category
  addProductDimensions(schema, product, unitSystem);

  // Add origin as country of origin if available
  if (product.origins) {
    schema.countryOfOrigin = {
      '@type': 'Country',
      'name': product.origins
    };
  }

  // Add ratings if available
  addRatingsIfAvailable(schema, product);

  // Add offers - either single or multiple currencies
  addProductOffers(schema, product, productURL, priceValidUntil, shopName);

  // Add additional properties based on product category
  addAdditionalProperties(schema, product);

  return schema;
}

/**
 * Get all product images for the schema
 * @param {object} product - Product data
 * @returns {string|string[]} - Single image URL or array of URLs
 */
function getProductImages(product) {
  // If using media object structure
  if (product.media) {
    return Object.values(product.media)
      .map(shotType => shotType.png?.url || shotType.webp?.url)
      .filter(Boolean)
      .sort((a, b) => {
        if (a.startsWith('A') && !b.startsWith('A')) return -1;
        if (!a.startsWith('A') && b.startsWith('A')) return 1;
        return 0;
      });
  }

  // If using mainImage structure
  if (product.mainImage?.png?.url) {
    return product.mainImage.png.url;
  }

  // If using images array
  if (product.images && Array.isArray(product.images)) {
    return product.images.filter(Boolean);
  }

  // Fallback
  return product.image || '';
}

/**
 * Add product dimensions to schema
 * @param {object} schema - Schema object to modify
 * @param {object} product - Product data
 * @param {string} unitSystem - Unit system (USC/SI)
 */
function addProductDimensions(schema, product, unitSystem) {
  const category = product.category || 'carpets';

  switch (category.toLowerCase()) {
    case 'carpets':
    case 'rugs':
      addCarpetDimensions(schema, product, unitSystem);
      break;
    // Add more category-specific dimension handlers here
    default:
      addGenericDimensions(schema, product, unitSystem);
      break;
  }
}

/**
 * Add carpet-specific dimensions to schema
 * @param {object} schema - Schema object to modify
 * @param {object} product - Product data
 * @param {string} unitSystem - Unit system (USC/SI)
 */
function addCarpetDimensions(schema, product, unitSystem) {
  // Handle dimensions from the new structure in the example
  if (product.measurements) {
    // Handle width and length from dimensions
    if (product.measurements.dimensions) {
      const dimensions = product.measurements.dimensions[unitSystem];

      if (dimensions) {
        // Add width if available
        if (dimensions.width) {
          schema.width = {
            '@type': 'QuantitativeValue',
            'value': dimensions.width,
            'unitText': unitSystem === 'USC' ? 'in' : 'cm'
          };
        }

        // Add length if available
        if (dimensions.length) {
          schema.height = {
            '@type': 'QuantitativeValue',
            'value': dimensions.length,
            'unitText': unitSystem === 'USC' ? 'in' : 'cm'
          };
        }
      }
    }

    // Handle pile thickness
    if (product.measurements.pile) {
      const pile = product.measurements.pile[unitSystem];

      if (pile && pile.thickness) {
        schema.depth = {
          '@type': 'QuantitativeValue',
          'value': pile.thickness,
          'unitText': pile.unitType || (unitSystem === 'USC' ? 'in' : 'mm')
        };
      }
    }

    // Handle weight
    if (product.measurements.weight) {
      const weight = product.measurements.weight[unitSystem];

      if (weight && weight.value) {
        schema.weight = {
          '@type': 'QuantitativeValue',
          'value': weight.value,
          'unitText': weight.unitType || (unitSystem === 'USC' ? 'lb' : 'kg')
        };
      }
    }
  } else {
    // Fallback to legacy structure
    addGenericDimensions(schema, product, unitSystem);
  }
}

/**
 * Add generic dimensions to schema (fallback for unknown categories or legacy structure)
 * @param {object} schema - Schema object to modify
 * @param {object} product - Product data
 * @param {string} unitSystem - Unit system (USC/SI)
 */
function addGenericDimensions(schema, product, unitSystem) {
  // Add width if available
  if (product.width) {
    schema.width = {
      '@type': 'QuantitativeValue',
      'value': product.width,
      'unitText': unitSystem === 'USC' ? 'in' : 'cm'
    };

    // Add imperial width as additional property if available
    if (unitSystem !== 'USC' && product.widthImperial) {
      schema.width.additionalProperty = [{
        '@type': 'PropertyValue',
        'name': 'imperial',
        'value': product.widthImperial,
        'unitText': 'ft/in'
      }];
    }
  }

  // Add height/length if available
  if (product.length) {
    schema.height = {
      '@type': 'QuantitativeValue',
      'value': product.length,
      'unitText': unitSystem === 'USC' ? 'in' : 'cm'
    };

    // Add imperial length as additional property if available
    if (unitSystem !== 'USC' && product.lengthImperial) {
      schema.height.additionalProperty = [{
        '@type': 'PropertyValue',
        'name': 'imperial',
        'value': product.lengthImperial,
        'unitText': 'ft/in'
      }];
    }
  }

  // Add thickness if available
  if (product.pileThickness) {
    schema.depth = {
      '@type': 'QuantitativeValue',
      'value': product.pileThickness,
      'unitText': unitSystem === 'USC' ? 'in' : 'mm'
    };

    // Add imperial thickness as additional property if available
    if (unitSystem !== 'USC' && product.pileThicknessInches) {
      schema.depth.additionalProperty = [{
        '@type': 'PropertyValue',
        'name': 'imperial',
        'value': product.pileThicknessInches,
        'unitText': 'inches'
      }];
    }
  }

  // Add weight if available
  if (product.weight) {
    schema.weight = {
      '@type': 'QuantitativeValue',
      'value': product.weight,
      'unitText': unitSystem === 'USC' ? 'lb' : 'kg'
    };

    // Add imperial weight as additional property if available
    if (unitSystem !== 'USC' && product.weightPounds) {
      schema.weight.additionalProperty = [{
        '@type': 'PropertyValue',
        'name': 'imperial',
        'value': product.weightPounds,
        'unitText': 'pounds'
      }];
    }
  }
}

/**
 * Add ratings to schema if available
 * @param {object} schema - Schema object to modify
 * @param {object} product - Product data
 */
function addRatingsIfAvailable(schema, product) {
  // Check if rating exists directly or in the rating object
  const ratingValue = product.rating?.value || product.rating || null;
  const ratingCount = product.rating?.count || product.ratingCount || 5;

  if (ratingValue) {
    schema.aggregateRating = {
      '@type': 'AggregateRating',
      'ratingValue': ratingValue,
      'reviewCount': ratingCount,
      'bestRating': 5,
      'worstRating': 1
    };
  }
}

/**
 * Add product offers to schema
 * @param {object} schema - Schema object to modify
 * @param {object} product - Product data
 * @param {string} productURL - Product URL
 * @param {string} priceValidUntil - Price valid until date
 * @param {string} shopName - Shop name
 */
function addProductOffers(schema, product, productURL, priceValidUntil, shopName) {
  // Check if we need to handle multiple currencies
  const hasMultipleCurrencies =
    (product.pricing && product.pricing.current) ||
    (product.priceGBP || product.priceUSD);

  if (hasMultipleCurrencies) {
    // If using pricing.current object with multiple currencies
    if (product.pricing && product.pricing.current) {
      const currencies = Object.keys(product.pricing.current);

      if (currencies.length > 1) {
        // Create AggregateOffer for multiple currencies
        schema.offers = {
          '@type': 'AggregateOffer',
          'priceCurrency': currencies[0],
          'lowPrice': Math.min(...currencies.map(c => Number(product.pricing.current[c]) || 0)),
          'highPrice': Math.max(...currencies.map(c => Number(product.pricing.current[c]) || 0)),
          'offerCount': currencies.length,
          'offers': currencies.map(currency => createSingleOffer(
            product,
            currency,
            product.pricing.current[currency],
            productURL,
            priceValidUntil,
            shopName
          ))
        };
        return;
      }
    }

    // If using individual price properties (priceGBP, priceUSD, etc.)
    if (product.priceGBP || product.priceUSD) {
      const baseCurrency = 'EUR';
      const basePrice = product.price;

      schema.offers = {
        '@type': 'AggregateOffer',
        'priceCurrency': baseCurrency,
        'lowPrice': Math.min(
          Number(basePrice) || Infinity,
          Number(product.priceGBP) || Infinity,
          Number(product.priceUSD) || Infinity
        ),
        'highPrice': Math.max(
          Number(basePrice) || 0,
          Number(product.priceGBP) || 0,
          Number(product.priceUSD) || 0
        ),
        'offerCount': 1 + (product.priceGBP ? 1 : 0) + (product.priceUSD ? 1 : 0),
        'offers': [
          createSingleOffer(product, baseCurrency, basePrice, productURL, priceValidUntil, shopName)
        ]
      };

      // Add GBP offer if available
      if (product.priceGBP) {
        schema.offers.offers.push(
          createSingleOffer(product, 'GBP', product.priceGBP, productURL, priceValidUntil, shopName)
        );
      }

      // Add USD offer if available
      if (product.priceUSD) {
        schema.offers.offers.push(
          createSingleOffer(product, 'USD', product.priceUSD, productURL, priceValidUntil, shopName)
        );
      }

      return;
    }
  }

  // Default: single offer with primary currency
  schema.offers = createSingleOffer(
    product,
    product.currency || 'EUR',
    product.price,
    productURL,
    priceValidUntil,
    shopName
  );
}

/**
 * Create a single offer object
 * @param {object} product - Product data
 * @param {string} currency - Currency code
 * @param {number} price - Price amount
 * @param {string} productURL - Product URL
 * @param {string} priceValidUntil - Price valid until date
 * @param {string} shopName - Shop name
 * @returns {object} - Offer object
 */
function createSingleOffer(product, currency, price, productURL, priceValidUntil, shopName) {
  // Validate price
  if (!isValidPrice(price)) {
    console.warn(`Invalid price value: ${price}`);
    price = 0;
  }

  const isInStock = product.availability || product.stock > 0;

  const offer = {
    '@type': 'Offer',
    'url': productURL,
    'priceCurrency': currency,
    'price': price,
    'priceValidUntil': priceValidUntil,
    'availability': isInStock
      ? 'https://schema.org/InStock'
      : 'https://schema.org/OutOfStock',
    'seller': {
      '@type': 'Organization',
      'name': shopName
    }
  };

  // Add shipping details if available
  if (product.shipping || product.shippingPrice) {
    offer.shippingDetails = {
      '@type': 'OfferShippingDetails',
      'shippingRate': {
        '@type': 'MonetaryAmount',
        'value': product.shipping?.cost || product.shippingPrice || 0,
        'currency': product.shipping?.currency || currency
      },
      'deliveryTime': {
        '@type': 'ShippingDeliveryTime',
        'handlingTime': {
          '@type': 'QuantitativeValue',
          'minValue': product.shipping?.handlingTime?.min || 1,
          'maxValue': product.shipping?.handlingTime?.max || 3,
          'unitCode': 'DAY'
        },
        'transitTime': {
          '@type': 'QuantitativeValue',
          'minValue': product.shipping?.transitTime?.min || 1,
          'maxValue': product.shipping?.transitTime?.max || 5,
          'unitCode': 'DAY'
        }
      }
    };

    // Add shipping destinations if available
    if (allowedCountries && allowedCountries.length) {
      offer.shippingDetails.shippingDestination = allowedCountries.map(country => ({
        '@type': 'DefinedRegion',
        'addressCountry': country
      }));
    }
  }

  return offer;
}

/**
 * Add additional properties to product schema
 * @param {object} schema - Schema object to modify
 * @param {object} product - Product data
 */
function addAdditionalProperties(schema, product) {
  const category = product.category || 'carpets';
  const additionalProperties = [];

  // Add common properties for all product types
  if (product.style) {
    additionalProperties.push({
      '@type': 'PropertyValue',
      'name': 'Style',
      'value': product.style
    });
  }

  // Add category-specific properties
  switch (category.toLowerCase()) {
    case 'carpets':
    case 'rugs':
      addCarpetProperties(additionalProperties, product);
      break;
    // Add more category-specific property handlers here
    default:
      // No additional properties for unknown categories
      break;
  }

  // Only add to schema if we have properties
  if (additionalProperties.length > 0) {
    schema.additionalProperty = additionalProperties;
  }
}

/**
 * Add carpet-specific properties
 * @param {Array} additionalProperties - Array of additional properties
 * @param {object} product - Product data
 */
function addCarpetProperties(additionalProperties, product) {
  // Add fringes info if available
  if (product.hasFringes !== undefined) {
    additionalProperties.push({
      '@type': 'PropertyValue',
      'name': 'Fringes',
      'value': product.hasFringes ? 'Yes' : 'No'
    });
  }

  // Add pile density if available from new structure
  if (product.measurements && product.measurements.density) {
    const density = product.measurements.density.SI || product.measurements.density;
    if (density && density.value) {
      additionalProperties.push({
        '@type': 'PropertyValue',
        'name': 'Pile Density',
        'value': `${density.value} ${density.unitType || 'g/m²'}`
      });
    }
  } 
  // Fallback to legacy structure
  else if (product.density) {
    additionalProperties.push({
      '@type': 'PropertyValue',
      'name': 'Pile Density',
      'value': `${product.density} gr/m²`
    });
  }

  // Add medallion info if available
  if (product.hasMedallion !== undefined) {
    additionalProperties.push({
      '@type': 'PropertyValue',
      'name': 'Medallion',
      'value': product.hasMedallion ? 'Yes' : 'No'
    });
  }

  // Add foundation material if available
  if (product.foundationMaterial) {
    additionalProperties.push({
      '@type': 'PropertyValue',
      'name': 'Foundation Material',
      'value': product.foundationMaterial
    });
  }
}

/**
 * Export a function to create product schema as JSON string
 * @param {object} product - Product data
 * @param {string} lang - Language code
 * @param {string} url - Product URL (optional)
 * @returns {string} - JSON string of product schema
 */
export function createProductSchemaString(product, lang, url = null) {
  const schema = generateProductSchema(product, lang, url);
  return schema ? JSON.stringify(schema) : '{}';
}
